import { useState, useEffect } from 'react';
import { useAppStore } from '../store/useAppStore';
import {
  getUserProjects,
  createProject,
  updateProject,
  deleteProject,
  getProjectById
} from '../utils/projectOperations';

export interface Project {
  id: string;
  hy_user_id: string;
  project_name: string;
  project_description?: string;
  thumbnail_url?: string;
  project_color: string;
  asset_count: number;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectData {
  project_name: string;
  project_description?: string;
  thumbnail_url?: string;
  project_color?: string;
}

export interface UpdateProjectData {
  project_name?: string;
  project_description?: string;
  thumbnail_url?: string;
  project_color?: string;
  is_archived?: boolean;
}

export const useProjects = (includeArchived: boolean = false) => {
  const { session } = useAppStore();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = async () => {
    console.log('🔍 [PROJECTS] Fetching projects for user:', session?.hy_user_id);

    if (!session || !session.hy_user_id) {
      console.warn('⚠️ [PROJECTS] No session or hy_user_id found - cannot fetch projects');
      setLoading(false);
      setError('Session not available - cannot fetch projects');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log(`📤 [PROJECTS] Calling getUserProjects for user:`, {
        userId: session.hy_user_id,
        includeArchived
      });

      const result = await getUserProjects(session.hy_user_id, includeArchived);

      console.log('📥 [PROJECTS] getUserProjects result:', result);

      if (result.success && result.projects) {
        console.log(`✅ [PROJECTS] Loaded ${result.projects.length} projects for user ${session.hy_user_id}`);
        setProjects(result.projects);
      } else {
        console.error('❌ [PROJECTS] Failed to fetch projects:', result.error);
        setError(result.error || 'Failed to fetch projects');
        setProjects([]);
      }
    } catch (err) {
      console.error('💥 [PROJECTS] Exception while fetching projects:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const createNewProject = async (data: CreateProjectData): Promise<{ success: boolean; project?: Project; error?: string }> => {
    if (!session || !session.hy_user_id) {
      return { success: false, error: 'Session not available' };
    }

    try {
      console.log('📤 [PROJECTS] Creating new project:', data);
      const result = await createProject(session.hy_user_id, data);

      if (result.success && result.project) {
        console.log('✅ [PROJECTS] Project created successfully:', result.project.id);
        await fetchProjects(); // Refresh the list
        return { success: true, project: result.project };
      } else {
        console.error('❌ [PROJECTS] Failed to create project:', result.error);
        return { success: false, error: result.error || 'Failed to create project' };
      }
    } catch (err) {
      console.error('💥 [PROJECTS] Exception while creating project:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  const updateExistingProject = async (projectId: string, data: UpdateProjectData): Promise<{ success: boolean; error?: string }> => {
    if (!session || !session.hy_user_id) {
      return { success: false, error: 'Session not available' };
    }

    try {
      console.log('📤 [PROJECTS] Updating project:', projectId, data);
      const result = await updateProject(projectId, session.hy_user_id, data);

      if (result.success) {
        console.log('✅ [PROJECTS] Project updated successfully:', projectId);
        await fetchProjects(); // Refresh the list
        return { success: true };
      } else {
        console.error('❌ [PROJECTS] Failed to update project:', result.error);
        return { success: false, error: result.error || 'Failed to update project' };
      }
    } catch (err) {
      console.error('💥 [PROJECTS] Exception while updating project:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  const deleteExistingProject = async (projectId: string): Promise<{ success: boolean; error?: string }> => {
    if (!session || !session.hy_user_id) {
      return { success: false, error: 'Session not available' };
    }

    try {
      console.log('📤 [PROJECTS] Deleting project:', projectId);
      const result = await deleteProject(projectId, session.hy_user_id);

      if (result.success) {
        console.log('✅ [PROJECTS] Project deleted successfully:', projectId);
        await fetchProjects(); // Refresh the list
        return { success: true };
      } else {
        console.error('❌ [PROJECTS] Failed to delete project:', result.error);
        return { success: false, error: result.error || 'Failed to delete project' };
      }
    } catch (err) {
      console.error('💥 [PROJECTS] Exception while deleting project:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  const getProject = async (projectId: string): Promise<{ success: boolean; project?: Project; error?: string }> => {
    if (!session || !session.hy_user_id) {
      return { success: false, error: 'Session not available' };
    }

    try {
      console.log('📤 [PROJECTS] Getting project:', projectId);
      const result = await getProjectById(projectId, session.hy_user_id);

      if (result.success && result.project) {
        console.log('✅ [PROJECTS] Project retrieved successfully:', projectId);
        return { success: true, project: result.project };
      } else {
        console.error('❌ [PROJECTS] Failed to get project:', result.error);
        return { success: false, error: result.error || 'Failed to get project' };
      }
    } catch (err) {
      console.error('💥 [PROJECTS] Exception while getting project:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  useEffect(() => {
    console.log('🔄 [PROJECTS] useEffect triggered - session changed:', {
      hasSession: !!session,
      userId: session?.hy_user_id,
      includeArchived
    });

    if (session?.hy_user_id) {
      fetchProjects();
    }
  }, [session?.hy_user_id, includeArchived]);

  return {
    projects,
    loading,
    error,
    refetch: fetchProjects,
    createProject: createNewProject,
    updateProject: updateExistingProject,
    deleteProject: deleteExistingProject,
    getProject
  };
};
